import { Injectable, signal, inject } from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable, shareReplay, tap} from 'rxjs';
import {FeatureFlag} from '@/app/core/models/feature-flag/featureFlag';
import {environment} from '@/environments/environment';
import {NavigationEnd, Router} from '@angular/router';
import {filter} from 'rxjs/operators';
import {AuthService} from '@services/auth.service';
import {FeatureFlagKey} from '@/app/core/models/feature-flag/featureFlagKey';

@Injectable({ providedIn: 'root' })
export class FeatureFlagService {

	private readonly SERVICE_URI = `${environment.backendUrl}/feature-flags`;

	private http = inject(HttpClient);
	private router = inject(Router);
	private authService = inject(AuthService);

	private cache$?: Observable<FeatureFlag[]>;
	private lastRefresh = 0;
	private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
	private featureFlags = signal<Record<string, string>>({});

	constructor() {
		this.router.events.pipe(
			filter(event => event instanceof NavigationEnd)
		).subscribe(() => {
			if (this.isCacheExpired()) {
				this.refreshFlags();
			}
		});
	}

	getFeatureFlags(): Observable<FeatureFlag[]> {
		if (!this.cache$ || this.isCacheExpired()) {
			this.cache$ = this.http.get<FeatureFlag[]>(this.SERVICE_URI).pipe(
				tap(flags => {
					this.updateFlags(flags);
					this.lastRefresh = Date.now();
				}),
				shareReplay(1)
			);
		}
		return this.cache$;
	}

	isEnabled(flagName: FeatureFlagKey): boolean {
		const status = this.featureFlags()[flagName];

		if (status === 'ENABLED') {
			return true;
		}

		if (status === 'BETA_TEST') {
			return this.authService.isBetaTester();
		}

		return false;
	}

	isBeta(flagName: FeatureFlagKey): boolean {
		const status = this.featureFlags()[flagName];
		return status === 'BETA_TEST';
	}

	/** Strictement ENABLED (ignore le statut beta-testeur) — enforcement binaire du marketplace. */
	isStrictlyEnabled(flagName: FeatureFlagKey): boolean {
		return this.featureFlags()[flagName] === 'ENABLED';
	}

	refreshFlags(): void {
		this.cache$ = undefined;
		this.getFeatureFlags().subscribe();
	}

	private isCacheExpired(): boolean {
		return Date.now() - this.lastRefresh > this.CACHE_DURATION;
	}

	private updateFlags(flags: FeatureFlag[]): void {
		const flagsRecord: Record<string, string> = flags.reduce((acc, flag) => {
			acc[flag.name] = flag.status;
			return acc;
		}, {} as Record<string, string>);

		this.featureFlags.set(flagsRecord);
	}
}
